/**
 * NOTAM由来の高度制限区域（通称BluePZ）に関する純粋計算。
 *
 * 曜日・時間帯で内容が切り替わる「体制」の判定、点がゾーン内にあるかの
 * 判定（レイキャスティング法）、天井超過（侵入）の判定を持つ。
 * 会場データの読み込み・DOM・地図描画は apps/web 側の責務で、ここには
 * 一切依存しない（他の gv-core モジュールと同じ方針）。
 *
 * ★★ 「入間基地と事前調整済み」のような条件は、渡良瀬BluePZ固有の
 * 個別事情であり、このモジュールのどこにもハードコードしない。ゾーンが
 * 任意個の「条件付き高度引き上げ（NotamAltitudeOverride相当）」を
 * 持てる汎用的な仕組みにし、文言・対象体制・引き上げ後の高度は全て
 * 呼び出し側（データ）が持つ。
 */

import type { LatLon } from './dms.ts';

/**
 * レイキャスティング法による点内判定。ringは開いた頂点列（始点=終点を
 * 重複させない）。境界線上・頂点上ちょうどは「内側」とみなす（安全側に
 * 倒す：境界ちょうどを侵入とみなさない方が危険なので）。
 */
export function pointInPolygon(pt: LatLon, ring: LatLon[]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const a = ring[i];
    const b = ring[j];
    if (onSegment(pt, a, b)) return true;
    const intersect =
      a.lat > pt.lat !== b.lat > pt.lat &&
      pt.lon < ((b.lon - a.lon) * (pt.lat - a.lat)) / (b.lat - a.lat) + a.lon;
    if (intersect) inside = !inside;
  }
  return inside;
}

function onSegment(pt: LatLon, a: LatLon, b: LatLon): boolean {
  const cross = (b.lon - a.lon) * (pt.lat - a.lat) - (b.lat - a.lat) * (pt.lon - a.lon);
  if (Math.abs(cross) > 1e-9) return false;
  const dot = (pt.lon - a.lon) * (b.lon - a.lon) + (pt.lat - a.lat) * (b.lat - a.lat);
  const lenSq = (b.lon - a.lon) ** 2 + (b.lat - a.lat) ** 2;
  return dot >= 0 && dot <= lenSq;
}

// --- 体制（曜日・時間帯による切り替え） ---------------------------------------

export interface NotamOverrideChoice {
  /** null = 自動判定。指定時はそのregime idを強制的に使う */
  regimeId: string | null;
  /** 「今日は祝日として扱う」。trueなら平日でも週末体制を強制する */
  treatAsHoliday: boolean;
}

export interface RegimeResolutionInput {
  /** UTC epoch ms */
  nowUtcMs: number;
  /** 平日・土曜午前に使うregime id */
  weekdayRegimeId: string;
  /** 土曜午後・日祝に使うregime id */
  weekendRegimeId: string;
  /** 土曜のAM/PM切り替え時刻（JSTの時、0-23）。既定12 */
  saturdayCutoffHourJst?: number;
  override?: NotamOverrideChoice;
}

export interface RegimeResolution {
  regimeId: string;
  source: 'auto' | 'manual-regime' | 'manual-holiday';
}

/**
 * UTC epoch ms → JSTの曜日(0=日曜)と時を返す。
 * 日本にサマータイムは無いため、固定+9hの換算で十分（Intl/タイムゾーン
 * データへの依存を避ける）。
 */
export function jstWeekdayAndHour(utcMs: number): { weekday: number; hour: number } {
  const jst = new Date(utcMs + 9 * 3600_000);
  return { weekday: jst.getUTCDay(), hour: jst.getUTCHours() };
}

/** 現在どの体制が適用されるかを判定する。手動指定 > 祝日指定 > 自動判定の優先順位 */
export function resolveNotamRegime(input: RegimeResolutionInput): RegimeResolution {
  if (input.override?.regimeId) {
    return { regimeId: input.override.regimeId, source: 'manual-regime' };
  }
  if (input.override?.treatAsHoliday) {
    return { regimeId: input.weekendRegimeId, source: 'manual-holiday' };
  }
  const { weekday, hour } = jstWeekdayAndHour(input.nowUtcMs);
  const cutoff = input.saturdayCutoffHourJst ?? 12;
  if (weekday === 0) return { regimeId: input.weekendRegimeId, source: 'auto' }; // 日曜
  if (weekday === 6) {
    return { regimeId: hour < cutoff ? input.weekdayRegimeId : input.weekendRegimeId, source: 'auto' }; // 土曜
  }
  return { regimeId: input.weekdayRegimeId, source: 'auto' }; // 平日
}

// --- ゾーン・天井・侵入判定 ---------------------------------------------------

export interface NotamOverrideLike {
  id: string;
  altFt: number;
  /** 指定時はその体制がアクティブな時だけ有効。未指定なら常に候補になる */
  appliesToRegimeId?: string;
}

export interface NotamZoneLike {
  points: LatLon[];
  altFt: number;
  overrides?: NotamOverrideLike[];
}

export interface NotamRegimeLike {
  id: string;
  zones: NotamZoneLike[];
}

/**
 * ゾーンの実効天井を求める。activeOverrideIds に含まれ、かつ
 * appliesToRegimeId が現在の体制と一致（または未指定）するoverrideの
 * うち、最も高い値を使う。該当が無ければ通常のaltFt。
 */
export function effectiveCeilingFt(
  zone: NotamZoneLike,
  activeRegimeId: string,
  activeOverrideIds: ReadonlySet<string>,
): number {
  let ceiling = zone.altFt;
  for (const o of zone.overrides ?? []) {
    if (o.appliesToRegimeId && o.appliesToRegimeId !== activeRegimeId) continue;
    if (activeOverrideIds.has(o.id) && o.altFt > ceiling) ceiling = o.altFt;
  }
  return ceiling;
}

export interface NotamFix {
  lat: number;
  lon: number;
  /** 高度 [m] */
  altM: number;
}

export interface NotamIncursion {
  zone: NotamZoneLike;
  /** 適用中の実効天井 [ft]（override考慮後） */
  effectiveCeilingFt: number;
  /** 天井超過量 [ft]（正の値のみ返る） */
  marginFt: number;
}

const M_PER_FT = 0.3048;

/**
 * fixが現在の体制(activeRegime)のいずれかのゾーン内かつ実効天井を
 * 超えているかを判定する。天井「ちょうど」は超過とみなさない
 * （高度計のノイズを考慮し、常設バナーのみという選択と合わせて誤警報を
 * 避ける）。複数ゾーンにまたがる場合は最初に見つかった超過ゾーンを返す
 * （原典チャート上のゾーンは隙間なく分割されており重複は想定しない）。
 */
export function checkNotamIncursion(
  fix: NotamFix,
  activeRegime: NotamRegimeLike,
  activeOverrideIds: ReadonlySet<string>,
): NotamIncursion | null {
  const fixFt = fix.altM / M_PER_FT;
  for (const zone of activeRegime.zones) {
    if (!pointInPolygon({ lat: fix.lat, lon: fix.lon }, zone.points)) continue;
    const ceilingFt = effectiveCeilingFt(zone, activeRegime.id, activeOverrideIds);
    if (fixFt > ceilingFt) {
      return { zone, effectiveCeilingFt: ceilingFt, marginFt: fixFt - ceilingFt };
    }
  }
  return null;
}
